import { FC, useEffect, useRef, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  useDeleteContractDocumentMutation,
  useGetArchiveClientDocument,
  useRestoreArchiveClientDocument,
} from "../../../../../api/services/client/clientApis";
import {
  FormController,
  MuiBox,
  MuiDateField,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../../components";
import { MuiLink } from "../../../../../components/link/MuiLink";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../../components/statusBadge/StatusBadge";
import { MuiNotesToolTip } from "../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import {
  convertToUserFormat,
  getParsedErrorMessage,
  isEmpty,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import {
  ClientDocument,
  CreateClientDocumentsFormKey,
  DocumentsProps,
} from "../../../modelsAndSchemas/Models";
import { documentSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  CONTRACT_DOC_TYPE,
  DOCUMENT_SITE_CORE_KEY,
} from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { ArchiveDocumentListWrapper } from "./ArchiveDocumentList.style";

export const pageId = "create-client-other-document-list";

export const ArchiveDocumentList: FC<DocumentsProps> = ({
  rows,
  onDocumentView,
  successHandler,
  onDocumentEdit,
  isEditable = true,
}) => {
  const selectedDocumentIndexRef = useRef<ClientDocument>();

  const [showDeleteContractModal, setShowDeleteContractModal] = useState(false);

  const {
    mutateAsync: getArchiveClientDocument,
    data: archiveClientDocumentData,
    isSuccess: isGetArchiveClientDocumentSuccess,
  } = useGetArchiveClientDocument();

  const {
    mutateAsync: deleteContractDocument,
    isPending: isDeleteContractDocumentPending,
    isSuccess: isDeleteContractDocumentSuccess,
    isError: isDeleteContractDocumentError,
    error: deleteContractDocumentError,
  } = useDeleteContractDocumentMutation();

  const {
    mutateAsync: restoreArchiveDocument,
    isSuccess: isRestoredArchiveDocumentSuccess,
    isPending: isRestoreArchiveDocumentLoading,
    isError: isRestoreArchiveDocumentError,
    error: restoreArchiveDocumentError,
  } = useRestoreArchiveClientDocument();

  const dispatch = useDispatch();
  const [showRestoreDocumentModal, setShowRestoreDocumentModal] =
    useState(false);

  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const [filteredResults, setFilteredResults] = useState<ClientDocument[]>([
    ...rows,
  ]);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const { control, reset, setValue } = useForm({
    mode: "all",
    defaultValues: {
      [CreateClientDocumentsFormKey.documentName]: "",
      [CreateClientDocumentsFormKey.notes]: "",
      [CreateClientDocumentsFormKey.status]: "",
      [CreateClientDocumentsFormKey.documentType]: "",
      [CreateClientDocumentsFormKey.effectiveDate]: "",
      [CreateClientDocumentsFormKey.expirationDate]: "",
      [CreateClientDocumentsFormKey.exclusivityId]: "",
      [CreateClientDocumentsFormKey.geoScope]: "",
    },
  });

  const { loggedInUser } = useGetLoggedInUserDetails();

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  useEffect(() => {
    if (isDeleteContractDocumentSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: getLabel(documentSiteCoreKeys.documentDeleted),
        }),
      );
      successHandler();
    }
  }, [isDeleteContractDocumentSuccess]);

  const watchFormValue = useWatch({
    control,
  });

  useEffect(() => {
    const {
      exclusivityId,
      geoScope,
      documentName,
      expirationDate,
      documentType,
      effectiveDate,
      status,
    } = watchFormValue;

    const filteredDocuments = [...rows].filter((item) => {
      const matchesDocumentName = item.documentName
        .toLowerCase()
        .includes(documentName!.toLowerCase());

      const matchesExclusivityId = exclusivityId
        ? item.exclusivityId === exclusivityId
        : true;

      const matchesGeoScope = geoScope ? item.geoScope === geoScope : true;
      const matchDocumentType =
        isEmpty(documentType) ||
        documentType!.toLocaleLowerCase().includes(item.documentType);
      const matchesExpirationDate =
        isEmpty(expirationDate) ||
        convertToUserFormat(
          item?.expirationDate ?? "",
          userCountryDateFormat,
        ) === expirationDate;

      const matchesEffectiveDate =
        isEmpty(effectiveDate) ||
        convertToUserFormat(
          item?.effectiveDate ?? "",
          userCountryDateFormat,
        ) === effectiveDate;

      const matchesStatus =
        isEmpty(status) ||
        fetchValueByKeyOfDropdown(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.archiveStatusList,
          item.status,
        ).toLowerCase() === status!.toLowerCase();

      return (
        matchesDocumentName &&
        matchesExclusivityId &&
        matchesGeoScope &&
        matchesExpirationDate &&
        matchesStatus &&
        matchesEffectiveDate &&
        matchDocumentType
      );
    });

    setFilteredResults([...filteredDocuments]);
    resetPagination();
  }, [watchFormValue]);

  useEffect(() => {
    reset();
    resetPagination();

    setFilteredResults([...rows]);
  }, [rows]);

  const isLoading =
    isRestoreArchiveDocumentLoading || isDeleteContractDocumentPending;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
      }),
    );
  }, [isLoading]);

  const error = restoreArchiveDocumentError || deleteContractDocumentError;
  const isError =
    isRestoreArchiveDocumentError || isDeleteContractDocumentError;

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: getParsedErrorMessage(error),
        }),
      );
    }
  }, [isError]);

  const getSuccessMessage = () => {
    if (isRestoredArchiveDocumentSuccess) {
      return getLabel(documentSiteCoreKeys.restoreMsg);
    }
    return "";
  };

  useEffect(() => {
    if (isRestoredArchiveDocumentSuccess) {
      const msg = getSuccessMessage();
      dispatch(
        toggleToast({
          open: true,
          message: msg,
        }),
      );
      successHandler();
    }
  }, [isRestoredArchiveDocumentSuccess]);

  const getLabel = (key: string) => {
    return fetchLabel(documentSiteCoreKeys.documentsTitle, key);
  };

  const onRestoreClick = (row: ClientDocument) => {
    if (!showRestoreDocumentModal) {
      selectedDocumentIndexRef.current = row;
      setShowRestoreDocumentModal(true);
    }
  };

  useEffect(() => {
    if (isGetArchiveClientDocumentSuccess) {
      const data = archiveClientDocumentData;
      const isDocumentTypeContract =
        (selectedDocumentIndexRef.current?.documentType as string) ===
        CONTRACT_DOC_TYPE;
      onDocumentView &&
        onDocumentView({
          ...data,
          status: data.status,
          documentType: isDocumentTypeContract
            ? DOCUMENT_SITE_CORE_KEY.documentTypeContract
            : DOCUMENT_SITE_CORE_KEY.documentTypeOthers,
        });
    }
  }, [isGetArchiveClientDocumentSuccess]);

  const fetchDocumentData = async (document: ClientDocument) => {
    selectedDocumentIndexRef.current = document;
    getArchiveClientDocument({
      documentId: document.documentId,
      documentType: document.documentType,
    });
  };

  const handleEditContractDocumentClick = (
    documentId: string,
    documentType: string,
  ) => {
    onDocumentEdit?.(
      documentId,
      documentType === CONTRACT_DOC_TYPE
        ? DOCUMENT_SITE_CORE_KEY.documentTypeContract
        : DOCUMENT_SITE_CORE_KEY.documentTypeOthers,
    );
  };

  const columns = [
    {
      id: CreateClientDocumentsFormKey.documentName,
      label: getLabel(documentSiteCoreKeys.documentName),
      isSortVisible: true,
      className: "cell-documentName",
      render: (row: any) =>
        !row.documentName ? null : (
          <MuiLink
            onClick={() => {
              fetchDocumentData(row);
            }}
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link document-name-link"
                text={row.documentName}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
    },
    {
      id: CreateClientDocumentsFormKey.status,
      label: getLabel(documentSiteCoreKeys.status),
      isSortVisible: true,
      className: "cell-status",
      render: (row: any) => {
        const statusValue = fetchValueByKeyOfDropdown(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.archiveStatusList,
          row.status,
        );
        return (
          <MuiBox className="badge-status">
            <StatusBadge status={row.status} label={statusValue} />
          </MuiBox>
        );
      },
    },
    {
      id: CreateClientDocumentsFormKey.documentType,
      label: getLabel(documentSiteCoreKeys.documentType),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="document-type">
            {row.documentType}
          </MuiTypography>
        </MuiBox>
      ),
    },

    {
      id: CreateClientDocumentsFormKey.effectiveDate,
      label: getLabel(documentSiteCoreKeys.effectiveDate),
      isSortVisible: true,
      className: "cell-effectiveDate",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {row.effectiveDate
              ? convertToUserFormat(row.effectiveDate, userCountryDateFormat)
              : ""}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CreateClientDocumentsFormKey.expirationDate,
      label: getLabel(documentSiteCoreKeys.expirationDate),
      isSortVisible: true,
      className: "cell-expirationDate",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {row.expirationDate
              ? convertToUserFormat(row.expirationDate, userCountryDateFormat)
              : ""}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CreateClientDocumentsFormKey.geoScope,
      label: getLabel(documentSiteCoreKeys.geoScope),
      isSortVisible: true,
      className: "cell-geo-scope",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {fetchValueByKeyOfDropdown(
              documentSiteCoreKeys.documentsTitle,
              documentSiteCoreKeys.geoScopeList,
              row.geoScope,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CreateClientDocumentsFormKey.exclusivityId,
      label: getLabel(documentSiteCoreKeys.exclusivityId),
      isSortVisible: true,
      className: "cell-exclusivity",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {fetchValueByKeyOfDropdown(
              documentSiteCoreKeys.documentsTitle,
              documentSiteCoreKeys.exclusivityList,
              row.exclusivityId,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CreateClientDocumentsFormKey.notes,
      label: commonLabels.notes,
      className: "cell-notes",
      render: (row: any) => (
        <MuiBox>
          <MuiNotesToolTip message={row.notes} />
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabel(documentSiteCoreKeys.action),
      className: "cell-action",
      render: (row: any) => {
        const menuItems = [
          {
            onClick: () => onRestoreClick(row),
            dataTestId: `restore-action`,
            label: commonLabels.restore,
          },
        ];

        if (row.status === STATUS_BADGE_TYPE.expired) {
          menuItems.unshift(
            {
              onClick: () =>
                handleEditContractDocumentClick(
                  row.documentId,
                  row.documentType,
                ),
              dataTestId: `edit-action`,
              label: commonLabels.edit,
            },
            {
              onClick: () => deleteContractDocumentAction(row),
              dataTestId: `delete-action`,
              label: commonLabels.delete,
            },
          );
        }

        return <MuiOptionMenu menuItems={menuItems} />;
      },
    },
  ];

  const handleExpDateChange = (newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(CreateClientDocumentsFormKey.expirationDate, dateValue);
  };

  const handleDeleteContractDocumentClick = () => {
    const documentId = selectedDocumentIndexRef.current?.documentId ?? "";
    if (isNotEmpty(documentId)) {
      setShowDeleteContractModal(false);
      deleteContractDocument({ documentId });
    }
  };

  const deleteContractDocumentAction = (row: ClientDocument) => {
    if (!showDeleteContractModal) {
      selectedDocumentIndexRef.current = row;
      setShowDeleteContractModal(true);
    }
  };

  const handleEffectiveDateChange = (newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(CreateClientDocumentsFormKey.effectiveDate, dateValue);
  };

  const tableHeaderForm = [
    <FormController
      key="documentName"
      name={CreateClientDocumentsFormKey.documentName}
      control={control}
    >
      <MuiTextField
        id={`${pageId}-${CreateClientDocumentsFormKey.documentName}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        placeholder={commonLabels.search}
        aria-label={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        showCloseIcon
      />
    </FormController>,
    <FormController
      key="status"
      name={CreateClientDocumentsFormKey.status}
      control={control}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.archiveStatusList,
          true,
        )}
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.status}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key="documentType"
      name={CreateClientDocumentsFormKey.documentType}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.documentTypeList,
          true,
        )}
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.notes}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key="effectiveDate"
      name={CreateClientDocumentsFormKey.effectiveDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          onChange={handleEffectiveDateChange}
          clearable
          selectDateARIALabel={commonARIALabel.selectDate}
          requiredARIALabel={commonARIALabel.required}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      key="expirationDate"
      name={CreateClientDocumentsFormKey.expirationDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          onChange={handleExpDateChange}
          format={userCountryDateFormat}
          clearable
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      key="geoScope"
      name={CreateClientDocumentsFormKey.geoScope}
    >
      <MuiSelect
        visibleDefaultSelectOption={true}
        label=""
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.geoScopeList,
        )}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.geoScope}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key="exclusivity"
      name={CreateClientDocumentsFormKey.exclusivityId}
    >
      <MuiSelect
        visibleDefaultSelectOption={true}
        label=""
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.exclusivityList,
        )}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.exclusivityId}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <></>,
  ];

  const handleCloseArchiveModal = () => {
    setShowRestoreDocumentModal(false);
  };

  const handleModalClose = () => {
    setShowDeleteContractModal(false);
  };

  const handleRestoreArchive = () => {
    const { documentType, documentId } = selectedDocumentIndexRef.current!;
    restoreArchiveDocument({ documentId, documentType });
    setShowRestoreDocumentModal(false);
  };

  return (
    <ArchiveDocumentListWrapper>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        isLastColumnSticky={isEditable}
        isShowDashIfValueEmpty
        tableResults={[...filteredResults]}
        noDataInApi={rows.length === 0}
        noResultMessage={commonLabels.noResult}
        emptyMessage={getLabel(
          documentSiteCoreKeys.noRecordsAvailableArchiveTabMessage,
        )}
        priorityResults={[]}
        isFooterVisible={false}
      />
      {showRestoreDocumentModal && (
        <WarningConfirmationModal
          show={true}
          onClose={handleCloseArchiveModal}
          onPrimaryButtonClick={handleRestoreArchive}
          labelContent={getLabel(documentSiteCoreKeys.restoreConfirmationText)}
        />
      )}
      {showDeleteContractModal && (
        <WarningConfirmationModal
          show={showDeleteContractModal}
          onClose={handleModalClose}
          onPrimaryButtonClick={handleDeleteContractDocumentClick}
          labelContent={getLabel(documentSiteCoreKeys.deleteDocumentActionText)}
        />
      )}
    </ArchiveDocumentListWrapper>
  );
};
